import React, { cloneElement, isValidElement, ReactElement, ReactNode } from 'react';
import styled from '@emotion/styled';
import classNames from 'classnames';
import { text_styles } from '../../foundation';
import { InputStatus } from '../input/types';
import { getFormHelperStatus } from './styles';
import { buildCSSWithLength, CSSValueWithLength } from '../../styles';
import {
  IconCircleCheckFillBold,
  IconCircleInfoFillBold,
  IconCircleWarningFillBold,
  IconProps,
} from '../icons/generated';
import { css } from '@emotion/react';

type FormHelperTextElement = keyof Pick<JSX.IntrinsicElements, 'div' | 'p' | 'span'>;

export interface FormHelperTextProps extends React.Attributes {
  children?: ReactNode;
  icon?: ReactElement<IconProps> | boolean;
  /** @default 'span' */
  as?: FormHelperTextElement;
  /** @default 'info' */
  status?: InputStatus;
  /** @deprecated FormHelperText 사용처에서 margin 마이그레이션 이후 스펙아웃될 예정입니다.  */
  mt?: CSSValueWithLength;
}

const STATUS_ICON: Record<InputStatus, ReactElement<IconProps>> = {
  info: <IconCircleInfoFillBold size={14} color='currentColor' />,
  success: <IconCircleCheckFillBold size={14} color='currentColor' />,
  error: <IconCircleWarningFillBold size={14} color='currentColor' />,
};

export const FormHelperText: React.FC<
  FormHelperTextProps & JSX.IntrinsicElements[Extract<FormHelperTextProps['as'], string>]
> = ({ as = 'span', status = 'info', icon, children, className, mt, ...props }) => {
  const _icon = isValidElement(icon)
    ? cloneElement(icon, { size: 14, color: icon.props.color ?? 'currentColor' })
    : icon;

  return (
    <HelperTextBase
      className={classNames('pds-form-helper-text', className)}
      css={getFormHelperStatus(status)}
      as={as}
      mt={mt}
      hasIcon={!!icon}
      {...props}
    >
      {icon && (
        <IconWrapper className='pds-form-helper-text__icon' aria-hidden>
          {_icon === true ? STATUS_ICON[status] : _icon || null}
        </IconWrapper>
      )}
      {children}
    </HelperTextBase>
  );
};

interface HelperTextBaseProps extends FormHelperTextProps {
  hasIcon: boolean;
}

const HelperTextBase = styled.span<HelperTextBaseProps>`
  ${({ hasIcon }) =>
    hasIcon
      ? css`
          display: inline-flex;
          align-items: flex-start;
          gap: 4px;
        `
      : css`
          display: inline-block;
        `}
  width: auto;
  ${text_styles.Body_12_Medium};
  ${({ mt }) => buildCSSWithLength('margin-top', mt)}
`;
const IconWrapper = styled.span`
  flex-shrink: 0;
  height: 14px;
  margin-top: 3px;
`;
